import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, ShoppingBag, Globe2, ArrowUpRight, Send, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";
import { SAFE_BAND_URL } from "@/lib/assets";
import { Reveal } from "@/components/Section";
import { SEO } from "@/components/SEO";
import { PAGE_SEO } from "@/lib/seoData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = [
  {
    icon: Mail,
    label: "Partnerships, Investment & Inquiries",
    value: "greg@wearspray.com",
    href: "mailto:greg@wearspray.com",
    note: "For partnerships, investment, beauty & personal care brands, health organizations, media, licensing, and everything else.",
  },
  {
    icon: ShoppingBag,
    label: "Buy Safe Band Pro",
    value: "SafeBandPro.com",
    href: SAFE_BAND_URL,
    note: "Purchase the device directly, then fill it with whatever you choose.",
    external: true,
  },
];

const TYPES = ["Partnership", "Clinical Collaboration", "Brand Licensing & Product Development", "Strategic Investment", "Media", "Other"];

export default function Contact() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    inquiry_type: "Partnership",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [error, setError] = useState("");

  // Deep-linkable topic preselection, e.g. /contact?topic=Clinical%20Collaboration
  // from CTAs like "Discuss a Clinical Pilot" elsewhere on the site.
  useEffect(() => {
    const topic = searchParams.get("topic");
    if (topic && TYPES.includes(topic)) {
      setForm((f) => ({ ...f, inquiry_type: topic }));
    }
  }, [searchParams]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/inquiries`, form);
      // GA4 conversion, fires only after a genuinely successful submission.
      // window.gtag exists only on the live production domain (see Analytics.js),
      // so this is a no-op on preview/localhost. No PII is sent.
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", {
          form_name: "contact_inquiry",
          inquiry_type: form.inquiry_type,
        });
      }
      setStatus("success");
      setForm({ name: "", email: "", organization: "", inquiry_type: "Partnership", message: "" });
    } catch (err) {
      setStatus("error");
      setError("Something went wrong. Please email greg@wearspray.com directly.");
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO {...PAGE_SEO["/contact"]} path="/contact" />
      <section className="relative pt-40 pb-16 md:pt-48 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[120vw] h-[55vh] radial-glow opacity-60" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-mono text-xs tracking-[0.25em] uppercase text-accent mb-6"
            data-testid="contact-hero"
          >
            Let's Build Together
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.0] max-w-4xl"
          >
            Accelerate the next generation of{" "}
            <span className="text-accent">wearable tech.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="font-body text-lg md:text-xl text-zinc-400 mt-8 max-w-2xl leading-relaxed"
          >
            We're seeking partners and investors who share our vision for
            accessible, on-demand protection. Send a note below or reach out
            directly.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.28 }}
          >
            <Link
              to="/investors"
              data-testid="contact-pitch-deck-link"
              className="inline-flex items-center gap-2 font-body text-sm text-accent border-b border-accent/40 hover:border-accent transition-colors mt-6"
            >
              Considering an investment? View our pitch deck
              <ArrowUpRight size={14} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FORM + CHANNELS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Form */}
        <div className="lg:col-span-7">
          <Reveal>
            <div className="border border-zinc-800 bg-[#0d0d0d] p-8 md:p-10">
              {status === "success" ? (
                <div data-testid="inquiry-success" className="flex flex-col items-start py-10">
                  <CheckCircle2 size={40} className="text-accent" strokeWidth={1.5} />
                  <h3 className="font-heading text-2xl font-medium mt-6">Message received.</h3>
                  <p className="font-body text-zinc-400 mt-3 leading-relaxed">
                    Thank you for reaching out. We'll get back to you shortly.
                  </p>
                  <button
                    data-testid="inquiry-reset"
                    onClick={() => setStatus("idle")}
                    className="font-body text-sm text-accent mt-6 border-b border-transparent hover:border-[#00e5ff] transition-colors"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="inquiry-form" className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <Field label="Name *">
                      <input
                        data-testid="inquiry-name"
                        required
                        value={form.name}
                        onChange={update("name")}
                        className="ws-input"
                        placeholder="Your name"
                      />
                    </Field>
                    <Field label="Email *">
                      <input
                        data-testid="inquiry-email"
                        required
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        className="ws-input"
                        placeholder="you@company.com"
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <Field label="Organization">
                      <input
                        data-testid="inquiry-org"
                        value={form.organization}
                        onChange={update("organization")}
                        className="ws-input"
                        placeholder="Company / NGO"
                      />
                    </Field>
                    <Field label="Inquiry Type">
                      <select
                        data-testid="inquiry-type"
                        value={form.inquiry_type}
                        onChange={update("inquiry_type")}
                        className="ws-input appearance-none cursor-pointer"
                      >
                        {TYPES.map((t) => (
                          <option key={t} value={t} className="bg-[#121212]">{t}</option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  <Field label="Message *">
                    <textarea
                      data-testid="inquiry-message"
                      required
                      rows={5}
                      value={form.message}
                      onChange={update("message")}
                      className="ws-input resize-none"
                      placeholder="Tell us about your interest in WearSpray…"
                    />
                  </Field>

                  {status === "error" && (
                    <p data-testid="inquiry-error" className="font-body text-sm text-red-400">{error}</p>
                  )}

                  <button
                    type="submit"
                    data-testid="inquiry-submit"
                    disabled={status === "sending"}
                    className="group inline-flex items-center gap-2 font-body text-sm font-medium bg-[#00e5ff] text-black px-8 py-4 hover:bg-[#00b3cc] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === "sending" ? (
                      <><Loader2 size={16} className="animate-spin" /> Sending…</>
                    ) : (
                      <>Send Message <Send size={16} className="group-hover:translate-x-0.5 transition-transform" /></>
                    )}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>

        {/* Channels */}
        <div className="lg:col-span-5 flex flex-col gap-px bg-zinc-800 border border-zinc-800">
          {CHANNELS.map((c, i) => (
            <Reveal key={c.label} delay={i * 0.1}>
              <a
                href={c.href}
                target={c.external ? "_blank" : undefined}
                rel={c.external ? "noopener noreferrer" : undefined}
                data-testid={`contact-channel-${i}`}
                className="group flex flex-col bg-[#121212] p-8 hover:bg-[#161616] transition-colors duration-500"
              >
                <div className="w-11 h-11 border border-zinc-700 flex items-center justify-center text-accent group-hover:border-[#00e5ff] transition-colors">
                  <c.icon size={20} strokeWidth={1.5} />
                </div>
                <span className="font-mono text-xs tracking-[0.15em] uppercase text-zinc-500 mt-6">
                  {c.label}
                </span>
                <span className="font-heading text-lg md:text-xl font-medium mt-2 flex items-center gap-2 text-white group-hover:text-accent transition-colors">
                  {c.value}
                  <ArrowUpRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
                <span className="font-body text-sm text-zinc-500 mt-3 leading-relaxed">
                  {c.note}
                </span>
              </a>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-xs tracking-[0.15em] uppercase text-zinc-500 block mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
